import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Entry extends Record<PropertyKey, unknown> {
  id: string
  action: HistoryAction
  author_id?: string | null
  author_email?: string | null
  author_first?: string | null
  author_last?: string | null
  patient_client?: string | null
  patient_id?: string | null
  patient_first?: string | null
  patient_last?: string | null
  created_at?: string | null
  document_id: string | null
}

const enum HistoryAction {
  Read = 'read',
  Write = 'write',
  DocumentRead = 'document_read',
  DocumentCreate = 'document_create',
  DocumentUpdate = 'document_update',
  DocumentDelete = 'document_delete'
}

let actionMapper: Record<HistoryAction, string> = {
  [HistoryAction.Read]: 'Read',
  [HistoryAction.Write]: 'Write',
  [HistoryAction.DocumentRead]: 'Document Read',
  [HistoryAction.DocumentUpdate]: 'Document Edit',
  [HistoryAction.DocumentDelete]: 'Document Delete',
  [HistoryAction.DocumentCreate]: 'Document Create'
}

// const enum MobileAttemptStatus {
//   Started = 'started',
//   Finished = 'finished',
//   Failed = 'failed'
// }

// let mobileAttemptStatusMapper: Record<MobileAttemptStatus, string> = {
//   [MobileAttemptStatus.Started]: 'Started',
//   [MobileAttemptStatus.Finished]: 'Finished',
//   [MobileAttemptStatus.Failed]: 'Failed'
// }

type QueryKey = [
  'behavioral-health-history',
  SearchParams
]

interface QueryData {
  data: Entry[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string

  filters: {
    action: HistoryAction[]
  }

  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'created_at',
  'patient_name',
  'author_name',
  'author_email',
  'action'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      action: []
    },
    sort: {
      field: 'created_at',
      direction: 'desc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  patient_name: 'patient_last',
  author_name: 'author_last'
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.action.length === initialState.searchParams.filters.action.length &&
    searchParams.filters.action.every(v => initialState.searchParams.filters.action.includes(v))
  )
}

export function BehavioralHistoryPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState)
  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'behavioral-health-history',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...filters.action.map(v => [ 'filters.action', v ]),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      return await http.getRequest(`/admin/behavioral?${sp.toString()}`)
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'created_at',
      header: 'Time',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        if (!entry.created_at) {
          return null
        }

        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.created_at)}
          </span>
        )
      }
    },
    {
      name: 'action',
      header: 'Action',
      isSortable: true,
      getValue (entry) {
        if (!entry.action) {
          return null
        }

        return actionMapper[entry.action] ?? entry.action
      }
    },
    {
      name: 'author_name',
      header: 'Author',
      isSortable: true,
      getValue (entry) {
        return [ entry.author_first, entry.author_last ].filter(Boolean).join(' ')
      }
    },
    {
      name: 'author_email',
      header: 'Email',
      isSortable: true
    },
    {
      name: 'patient_name',
      header: 'Patient',
      isSortable: true,
      getValue (entry) {
        return (
          <Link to={`/patient/${entry.patient_client ?? ''}/${entry.patient_id ?? ''}/patientFolder`}>
            {[ entry.patient_first, entry.patient_last ].filter(Boolean).join(' ')}
          </Link>
        )
      }
    },
    {
      name: 'document',
      header: 'Document',
      isSortable: true,
      getValue: (entry) => entry.document_id ?? null
    }
  ], [])

  let filters = useMemo(() => {
    return [
      {
        name: 'action',
        value: state.searchParams.filters.action,
        options: [
          { value: HistoryAction.Read, label: actionMapper[HistoryAction.Read] ?? HistoryAction.Read },
          { value: HistoryAction.Write, label: actionMapper[HistoryAction.Write] ?? HistoryAction.Write },
          { value: HistoryAction.DocumentRead, label: actionMapper[HistoryAction.DocumentRead] ?? HistoryAction.DocumentRead },
          { value: HistoryAction.DocumentCreate, label: actionMapper[HistoryAction.DocumentCreate] ?? HistoryAction.DocumentCreate },
          { value: HistoryAction.DocumentUpdate, label: actionMapper[HistoryAction.DocumentUpdate] ?? HistoryAction.DocumentUpdate },
          { value: HistoryAction.DocumentDelete, label: actionMapper[HistoryAction.DocumentDelete] ?? HistoryAction.DocumentDelete }
        ]
      }
    ]
  }, [ state.searchParams.filters ])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Behavioral Health History</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder="Search by action, author, author's email, document id or patient"
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No records found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
